import { motion } from "framer-motion";
import { Chapter, Reveal } from "@/components/landing/Reveal";

const BLOCKS = 20;

const ROWS = [
  { id: "research-engine", name: "Research Engine", filled: 20, status: "Complete", tone: "gold" },
  { id: "script-generation", name: "Script Generation", filled: 18, status: "In Development", tone: "gold" },
  { id: "storyboard", name: "Storyboard", filled: 10, status: "Building", tone: "amber" },
  { id: "narration", name: "Narration", filled: 4, status: "Planned", tone: "dim" },
  { id: "visual-production", name: "Visual Production", filled: 2, status: "Planned", tone: "dim" },
  { id: "video-assembly", name: "Video Assembly", filled: 0, status: "Planned", tone: "dim" },
];

const toneStyles = {
  gold: { fill: "text-[#D4AF37]", label: "text-[#D4AF37]" },
  amber: { fill: "text-[#E5A93C]/80", label: "text-[#E5A93C]" },
  dim: { fill: "text-white/30", label: "text-[#666666]" },
};

const StatusRow = ({ row, index }) => (
  <motion.div
    initial="hidden"
    whileInView="show"
    viewport={{ once: true, margin: "-60px" }}
    transition={{ staggerChildren: 0.018, delayChildren: index * 0.15 }}
    className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-[190px_1fr_150px] sm:items-baseline sm:gap-6"
    data-testid={`progress-bar-${row.id}`}
  >
    <motion.span
      variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
      className="text-xs tracking-[0.12em] text-[#F5F5F5] sm:text-sm"
    >
      {row.name}
    </motion.span>
    <span className="select-none whitespace-nowrap text-xs leading-none tracking-[0.08em] sm:text-sm" aria-label={`${row.filled} of ${BLOCKS} complete`}>
      {Array.from({ length: BLOCKS }).map((_, i) => (
        <motion.span
          key={i}
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
          className={i < row.filled ? toneStyles[row.tone].fill : "text-white/10"}
        >
          {i < row.filled ? "█" : "░"}
        </motion.span>
      ))}
    </span>
    <motion.span
      variants={{ hidden: { opacity: 0 }, show: { opacity: 1 } }}
      className={`text-[10px] uppercase tracking-[0.25em] sm:text-right ${toneStyles[row.tone].label}`}
    >
      {row.status}
    </motion.span>
  </motion.div>
);

export default function Status() {
  return (
    <section id="early-product" className="relative py-28 sm:py-36" data-testid="status-section">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Chapter index="05" label="Early Product" testid="status-chapter" />

        <Reveal>
          <h2
            className="max-w-3xl font-display text-3xl font-bold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl"
            data-testid="status-section-title"
          >
            We're building it, <span className="text-[#9A9A9A]">one production stage at a time.</span>
          </h2>
        </Reveal>

        <Reveal delay={0.2}>
          <div
            className="mt-16 border border-white/10 bg-[#0D0D0D] font-mono"
            data-testid="dev-status-card"
          >
            <div className="flex items-center justify-between border-b border-white/5 px-6 py-4 sm:px-8">
              <div className="flex items-center gap-3">
                <span className="h-2 w-2 animate-blink rounded-full bg-[#D4AF37]" />
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#9A9A9A] sm:text-xs">
                  pitsora — Build Status
                </span>
              </div>
              <span className="border border-[#D4AF37]/40 px-3 py-1 text-[9px] uppercase tracking-[0.25em] text-[#D4AF37] sm:text-[10px]">
                Early development
              </span>
            </div>

            <div className="divide-y divide-white/5 px-6 py-2 sm:px-8">
              {ROWS.map((row, i) => (
                <StatusRow key={row.id} row={row} index={i} />
              ))}
            </div>

            <div className="border-t border-white/5 px-6 py-4 text-[10px] tracking-[0.2em] text-[#555555] sm:px-8">
              {"// honest by design — updated as each stage ships"}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
